import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { FiFilter, FiColumns, FiDownload, FiPlus, FiTrash2, FiEdit2 } from 'react-icons/fi';
import { MdOutlinePushPin, MdPushPin } from 'react-icons/md';
import axios from 'axios';
import { useAuthFetch } from '../hooks/useAuthFetch';
import Icon13 from '../img/7481d4f175f497fa3ca635d9ea8974d559a00bf54762cdfe73ec49ac971c04f3.svg';

import { TooltipCell, ResetButton, ColumnVisibilityDropdown, SortIcon, sortIconDirection } from './common';
import { ColumnFilterDropdown, RangeFilterDropdown, DateRangeFilterDropdown, useFilterPopupPortal } from './common/filters';
import RowsIndicator from './common/RowsIndicator';
import { loadXLSX } from '../utils/xlsxLoader';
import { useUserPreference } from '../hooks/useUserPreference';
import { USER_PREFERENCE_KEYS } from '../constants/userPreferences';
import EquipmentLedgerModal from './EquipmentLedgerModal';
import { ConfirmDialog } from './common/ConfirmDialog';

const DEFAULT_COLUMN_ORDER = [
  'date', 'projectNo', 'projectName', 'activity', 'division', 'branchLocation', 'unitNumber', 'equipmentId', 'description', 'runTimeHours', 'dailyNotes', 'crewLeader', 'primaryDivision', 'updatedAtIso', 'actions'
];

const ITEMS_PER_PAGE = 15;
const INITIAL_LOAD_COUNT = 15;

const formatFullTimestamp = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  return `${mm}/${dd}/${yyyy}, ${hours}:${minutes}:${seconds} ${ampm}`;
};

const formatMMDDYYYY = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const formatHours = (value) => {
  if (value == null || value === '') return '--';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return String(value);
  return String(Math.round(parsed * 100) / 100);
};

/** Calendar date as YYYY-MM-DD. Avoids UTC parsing of YYYY-MM-DD vs local M/D/YYYY. */
const toCalendarISO = (value) => {
  if (!value) return '';
  const raw = String(value).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const slash = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    return `${slash[3]}-${slash[1].padStart(2, '0')}-${slash[2].padStart(2, '0')}`;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return '';
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const d = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const DEFAULT_VISIBLE_COLUMNS = [
  'date', 'projectNo', 'projectName', 'activity', 'division', 'unitNumber', 'description', 'runTimeHours'
];

function mergeColumnOrder(savedOrder) {
  if (!Array.isArray(savedOrder)) return DEFAULT_COLUMN_ORDER;
  const merged = [...savedOrder];
  DEFAULT_COLUMN_ORDER.forEach((key) => {
    if (!merged.includes(key)) merged.push(key);
  });
  return merged.filter((key) => DEFAULT_COLUMN_ORDER.includes(key));
}

function mergeVisibleColumns(savedColumns) {
  if (!Array.isArray(savedColumns) || savedColumns.length === 0) {
    return DEFAULT_VISIBLE_COLUMNS;
  }
  return savedColumns.filter(
    (key) => key !== 'actions' && DEFAULT_COLUMN_ORDER.includes(key)
  );
}

const DEFAULT_PINNED_COLUMNS = [];

function mergePinnedColumns(savedPins) {
  if (!Array.isArray(savedPins)) return DEFAULT_PINNED_COLUMNS;
  return savedPins.filter(
    (key) => key !== 'actions' && DEFAULT_COLUMN_ORDER.includes(key)
  );
}

const EMPTY_TEXT_FILTERS = {
  projectNo: '',
  projectName: '',
  unitNumber: '',
  equipmentId: '',
  description: '',
  dailyNotes: '',
};

const EMPTY_SELECT_FILTERS = {
  activity: [],
  division: [],
  branchLocation: [],
  crewLeader: [],
  primaryDivision: [],
  lastModifiedDate: [],
};

function EquipmentManagement() {
  const { getAuthConfig, isReady } = useAuthFetch();
  const [allData, setAllData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const {
    value: visibleColumns,
    setValue: setVisibleColumns,
    save: saveVisibleColumns,
  } = useUserPreference(
    USER_PREFERENCE_KEYS.EQUIPMENT_VISIBLE_COLUMNS,
    DEFAULT_VISIBLE_COLUMNS,
    {
      mergeWithDefault: mergeVisibleColumns,
      localStorageKey: 'equipment_visibleColumns',
    }
  );

  const {
    value: columnOrder,
    setValue: setColumnOrder,
    save: saveColumnOrder,
  } = useUserPreference(
    USER_PREFERENCE_KEYS.EQUIPMENT_COLUMN_ORDER,
    DEFAULT_COLUMN_ORDER,
    {
      mergeWithDefault: mergeColumnOrder,
      localStorageKey: 'equipment_columnOrder',
    }
  );

  const {
    value: pinnedColumns,
    setValue: setPinnedColumns,
    save: savePinnedColumns,
  } = useUserPreference(
    USER_PREFERENCE_KEYS.EQUIPMENT_PINNED_COLUMNS,
    DEFAULT_PINNED_COLUMNS,
    {
      mergeWithDefault: mergePinnedColumns,
      localStorageKey: 'equipment_pinnedColumns',
    }
  );

  const [searchTerm, setSearchTerm] = useState(() => localStorage.getItem('equipment_searchTerm') || '');

  const [columnFilters, setColumnFilters] = useState(() => {
    const saved = localStorage.getItem('equipment_columnFilters');
    return saved ? JSON.parse(saved) : { ...EMPTY_TEXT_FILTERS };
  });

  const [columnFilterValues, setColumnFilterValues] = useState(() => {
    const saved = localStorage.getItem('equipment_columnFilterValues');
    return saved ? JSON.parse(saved) : { ...EMPTY_SELECT_FILTERS };
  });

  const [dateRange, setDateRange] = useState(() => {
    const saved = localStorage.getItem('equipment_dateRange');
    return saved ? JSON.parse(saved) : { start: null, end: null };
  });

  const [runTimeRange, setRunTimeRange] = useState(() => {
    const saved = localStorage.getItem('equipment_runTimeRange');
    return saved ? JSON.parse(saved) : { min: null, max: null };
  });

  const [sortConfig, setSortConfig] = useState(() => {
    const saved = localStorage.getItem('equipment_sortConfig');
    return saved ? JSON.parse(saved) : { key: 'date', direction: 'desc' };
  });

  const [selectedRowId, setSelectedRowId] = useState(null);
  const [currentDisplayCount, setCurrentDisplayCount] = useState(INITIAL_LOAD_COUNT);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchEquipmentLedger = async (silent = false) => {
    if (!isReady) return;
    try {
      if (!silent) setLoading(true);
      setError(null);
      const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
      let allRecords = [];
      let offset = 0;
      const limit = 100;
      let hasMore = true;

      const fetchPage = async (pageOffset) => {
        const maxAttempts = 3;
        let lastError;
        for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
          try {
            return await axios.get(
              `${baseUrl}/api/miter/production-performance/equipment-ledger`,
              await getAuthConfig({ params: { limit, offset: pageOffset } })
            );
          } catch (err) {
            lastError = err;
            const message = err.response?.data?.message || err.message || '';
            const isTransient = /connection terminated|connection ended|ECONNRESET/i.test(message);
            if (!isTransient || attempt === maxAttempts - 1) throw err;
            await new Promise((resolve) => setTimeout(resolve, 400 * (attempt + 1)));
          }
        }
        throw lastError;
      };

      while (hasMore) {
        const response = await fetchPage(offset);
        if (response.data && response.data.status === 'success') {
          const records = response.data.data?.records || [];
          allRecords = [...allRecords, ...records];
          hasMore = response.data.data?.pagination?.hasMore || false;
          offset += limit;
        } else {
          throw new Error(response.data?.message || 'Failed to fetch equipment data');
        }
        if (limit <= 0 || allRecords.length > 10000) break;
      }

      setAllData(allRecords);
    } catch (err) {
      console.error('Error fetching equipment ledger:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch equipment data');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (isReady) fetchEquipmentLedger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
  const [columnSelectorPosition, setColumnSelectorPosition] = useState({ top: 0, left: 0 });
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);
  const columnButtonRef = useRef(null);
  const columnSelectorRef = useRef(null);
  const loadingRef = useRef(null);
  const tableScrollRef = useRef(null);
  const hScrollbarRef = useRef(null);
  const {
    filterDropdownRef,
    toggleFilterPopup,
    closeFilterPopup,
    renderFilterPopup,
    getFilterWrapperStyle,
  } = useFilterPopupPortal({
    activeFilterColumn,
    setActiveFilterColumn,
    tableScrollRef,
    hScrollbarRef,
  });
  const hScrollLastRef = useRef({ overflow: 0, scrollWidth: 0, clientWidth: 0 });
  const [hScroll, setHScroll] = useState({ overflow: 0, scrollWidth: 0, clientWidth: 0 });

  const originalColumns = useMemo(() => [
    { key: 'date', label: 'Date', width: 120, filterKey: 'date', filterable: true, type: 'date' },
    { key: 'projectNo', label: 'Project No.', width: 120, filterKey: 'projectNo', filterable: true, type: 'search' },
    { key: 'projectName', label: 'Project Name', width: 220, filterKey: 'projectName', filterable: true, type: 'search' },
    { key: 'activity', label: 'Activity', width: 180, filterKey: 'activity', filterable: true, type: 'select' },
    { key: 'division', label: 'Division', width: 120, filterKey: 'division', filterable: true, type: 'select' },
    { key: 'branchLocation', label: 'Branch (Location)', width: 140, filterKey: 'branchLocation', filterable: true, type: 'select' },
    { key: 'unitNumber', label: 'Unit Number', width: 130, filterKey: 'unitNumber', filterable: true, type: 'search' },
    { key: 'equipmentId', label: 'Equipment ID', width: 140, filterKey: 'equipmentId', filterable: true, type: 'search' },
    { key: 'description', label: 'Description', width: 220, filterKey: 'description', filterable: true, type: 'search' },
    { key: 'runTimeHours', label: 'Run Time (Hrs)', width: 130, filterKey: 'runTimeHours', filterable: true, type: 'range' },
    { key: 'dailyNotes', label: 'Daily Notes', width: 200, filterKey: 'dailyNotes', filterable: true, type: 'search' },
    { key: 'crewLeader', label: 'Crew Leader', width: 160, filterKey: 'crewLeader', filterable: true, type: 'select' },
    { key: 'primaryDivision', label: 'Primary Division', width: 150, filterKey: 'primaryDivision', filterable: true, type: 'select' },
    { key: 'updatedAtIso', label: 'Last Modified Date', width: 180, filterKey: 'lastModifiedDate', filterable: true, type: 'select' },
    { key: 'actions', label: 'Actions', width: 100, filterable: false, type: 'actions' },
  ], []);

  const columns = useMemo(() => {
    return [...originalColumns].sort((a, b) => {
      const aIdx = columnOrder.indexOf(a.key);
      const bIdx = columnOrder.indexOf(b.key);
      return (aIdx !== -1 ? aIdx : 99) - (bIdx !== -1 ? bIdx : 99);
    });
  }, [originalColumns, columnOrder]);

  const pinnedVisibleKeys = pinnedColumns.filter((key) => visibleColumns.includes(key));
  const unpinnedVisibleColumns = columns.filter(
    (column) =>
      column.key !== 'actions' &&
      visibleColumns.includes(column.key) &&
      !pinnedVisibleKeys.includes(column.key)
  );
  const actionsCol = columns.find((column) => column.key === 'actions');
  const visibleTableColumns = [
    ...pinnedVisibleKeys
      .map((key) => originalColumns.find((column) => column.key === key))
      .filter(Boolean),
    ...unpinnedVisibleColumns,
    ...(actionsCol ? [actionsCol] : []),
  ];
  const tableMinWidth = visibleTableColumns.reduce((sum, column) => sum + column.width, 0) || 1;
  const lastPinnedKey = pinnedVisibleKeys[pinnedVisibleKeys.length - 1] || null;

  const getPinnedLeftOffset = (columnKey) => {
    let left = 0;
    for (const key of pinnedVisibleKeys) {
      if (key === columnKey) return left;
      const col = originalColumns.find((c) => c.key === key);
      left += col?.width || 0;
    }
    return 0;
  };

  const pinnedColumnsWidth = pinnedVisibleKeys.reduce((sum, key) => {
    const col = originalColumns.find((c) => c.key === key);
    return sum + (col?.width || 0);
  }, 0);

  const getColumnStyle = (column, { isHeader = false } = {}) => {
    const isPinned = pinnedVisibleKeys.includes(column.key);
    if (isPinned) {
      return {
        width: `${column.width}px`,
        minWidth: `${column.width}px`,
        maxWidth: `${column.width}px`,
        position: 'sticky',
        left: `${getPinnedLeftOffset(column.key)}px`,
        zIndex: isHeader ? 30 : 4,
      };
    }
    return {
      minWidth: `${column.width}px`,
      width: `${(column.width / tableMinWidth) * 100}%`,
    };
  };

  useEffect(() => {
    localStorage.setItem('equipment_searchTerm', searchTerm);
  }, [searchTerm]);

  useEffect(() => {
    localStorage.setItem('equipment_columnFilters', JSON.stringify(columnFilters));
  }, [columnFilters]);

  useEffect(() => {
    localStorage.setItem('equipment_columnFilterValues', JSON.stringify(columnFilterValues));
  }, [columnFilterValues]);

  useEffect(() => {
    localStorage.setItem('equipment_dateRange', JSON.stringify(dateRange));
  }, [dateRange]);

  useEffect(() => {
    localStorage.setItem('equipment_runTimeRange', JSON.stringify(runTimeRange));
  }, [runTimeRange]);

  useEffect(() => {
    localStorage.setItem('equipment_sortConfig', JSON.stringify(sortConfig));
  }, [sortConfig]);

  useEffect(() => {
    if (!isColumnSelectorOpen) return undefined;
    function handleClickOutside(event) {
      if (columnSelectorRef.current && !columnSelectorRef.current.contains(event.target)) {
        setIsColumnSelectorOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isColumnSelectorOpen]);

  const handleColumnSelectorClick = () => {
    setIsColumnSelectorOpen((prev) => !prev);
  };

  useEffect(() => {
    if (isColumnSelectorOpen && columnButtonRef.current) {
      const updatePosition = () => {
        if (!columnButtonRef.current) return;
        const rect = columnButtonRef.current.getBoundingClientRect();
        const dropdownWidth = 192;
        let left = rect.left;
        const top = rect.bottom + 4;
        if (left + dropdownWidth > window.innerWidth - 8) left = rect.right - dropdownWidth;
        if (left < 8) left = 8;
        setColumnSelectorPosition({ top, left });
      };
      updatePosition();
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
    return undefined;
  }, [isColumnSelectorOpen]);

  const handleToggleColumn = (columnKey) => {
    if (columnKey === 'actions') return;
    const isHiding = visibleColumns.includes(columnKey);
    const newCols = isHiding
      ? visibleColumns.filter((key) => key !== columnKey)
      : [...visibleColumns, columnKey];
    setVisibleColumns(newCols);
    saveVisibleColumns(newCols);
    if (isHiding && pinnedColumns.includes(columnKey)) {
      const nextPins = pinnedColumns.filter((key) => key !== columnKey);
      setPinnedColumns(nextPins);
      savePinnedColumns(nextPins);
    }
  };

  const handleTogglePin = (columnKey) => {
    if (columnKey === 'actions') return;
    if (pinnedColumns.includes(columnKey)) {
      const nextPins = pinnedColumns.filter((key) => key !== columnKey);
      setPinnedColumns(nextPins);
      savePinnedColumns(nextPins);
      return;
    }
    if (!visibleColumns.includes(columnKey)) {
      const nextVisible = [...visibleColumns, columnKey];
      setVisibleColumns(nextVisible);
      saveVisibleColumns(nextVisible);
    }
    const nextPins = [...pinnedColumns, columnKey];
    setPinnedColumns(nextPins);
    savePinnedColumns(nextPins);
  };

  const handleColumnFilterSelect = (columnName, value) => {
    setColumnFilterValues((prev) => ({
      ...prev,
      [columnName]: prev[columnName]?.includes(value)
        ? prev[columnName].filter((v) => v !== value)
        : [...(prev[columnName] || []), value],
    }));
    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
    setHasMoreData(true);
  };

  const pendingColumnOrderRef = useRef(null);
  const pendingPinnedColumnsRef = useRef(null);
  const draggedHeaderKeyRef = useRef(null);
  const draggedHeaderWasPinnedRef = useRef(false);

  const handleReorderColumn = (fromIndex, toIndex, groupColumns) => {
    const fromKey = groupColumns[fromIndex].key;
    const toKey = groupColumns[toIndex].key;
    if (fromKey === 'actions' || toKey === 'actions') return;
    setColumnOrder((prevOrder) => {
      const newOrder = [...prevOrder];
      const idxA = newOrder.indexOf(fromKey);
      const idxB = newOrder.indexOf(toKey);
      if (idxA !== -1 && idxB !== -1) {
        const [movedItem] = newOrder.splice(idxA, 1);
        newOrder.splice(idxB, 0, movedItem);
      }
      pendingColumnOrderRef.current = newOrder;
      return newOrder;
    });
  };

  const handleReorderColumnEnd = () => {
    const orderToSave = pendingColumnOrderRef.current;
    pendingColumnOrderRef.current = null;
    if (orderToSave) saveColumnOrder(orderToSave);
    else saveColumnOrder();
  };

  const handleHeaderDragStart = (e, columnKey) => {
    if (columnKey === 'actions') return;
    if (e.target.closest?.('[data-pin-toggle]')) {
      e.preventDefault();
      return;
    }
    draggedHeaderKeyRef.current = columnKey;
    draggedHeaderWasPinnedRef.current = pinnedColumns.includes(columnKey);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', columnKey);
  };

  const handleHeaderDragOver = (e, targetKey) => {
    e.preventDefault();
    const draggedKey = draggedHeaderKeyRef.current;
    if (!draggedKey || draggedKey === targetKey || targetKey === 'actions') return;

    const draggedPinned = pinnedColumns.includes(draggedKey);
    const targetPinned = pinnedColumns.includes(targetKey);

    if (draggedPinned && targetPinned) {
      setPinnedColumns((prev) => {
        const next = [...prev];
        const idxA = next.indexOf(draggedKey);
        const idxB = next.indexOf(targetKey);
        if (idxA === -1 || idxB === -1) return prev;
        const [movedItem] = next.splice(idxA, 1);
        next.splice(idxB, 0, movedItem);
        pendingPinnedColumnsRef.current = next;
        return next;
      });
      return;
    }

    if (!draggedPinned && !targetPinned) {
      setColumnOrder((prevOrder) => {
        const newOrder = [...prevOrder];
        const idxA = newOrder.indexOf(draggedKey);
        const idxB = newOrder.indexOf(targetKey);
        if (idxA !== -1 && idxB !== -1) {
          const [movedItem] = newOrder.splice(idxA, 1);
          newOrder.splice(idxB, 0, movedItem);
        }
        pendingColumnOrderRef.current = newOrder;
        return newOrder;
      });
    }
  };

  const handleHeaderDragEnd = () => {
    const wasPinned = draggedHeaderWasPinnedRef.current;
    draggedHeaderKeyRef.current = null;
    draggedHeaderWasPinnedRef.current = false;

    if (wasPinned) {
      const pinsToSave = pendingPinnedColumnsRef.current;
      pendingPinnedColumnsRef.current = null;
      if (pinsToSave) savePinnedColumns(pinsToSave);
      else savePinnedColumns();
      return;
    }

    const orderToSave = pendingColumnOrderRef.current;
    pendingColumnOrderRef.current = null;
    if (orderToSave) saveColumnOrder(orderToSave);
    else saveColumnOrder();
  };

  const handleResetData = async () => {
    setSearchTerm('');
    setColumnFilters({ ...EMPTY_TEXT_FILTERS });
    setColumnFilterValues({ ...EMPTY_SELECT_FILTERS });
    setDateRange({ start: null, end: null });
    setRunTimeRange({ min: null, max: null });
    setSortConfig({ key: 'date', direction: 'desc' });
    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
    setHasMoreData(true);
    setLoadingMore(false);

    localStorage.removeItem('equipment_columnOrder');
    localStorage.removeItem('equipment_visibleColumns');
    localStorage.removeItem('equipment_pinnedColumns');
    localStorage.removeItem('equipment_runTimeRange');

    setColumnOrder(DEFAULT_COLUMN_ORDER);
    setVisibleColumns(DEFAULT_VISIBLE_COLUMNS);
    setPinnedColumns(DEFAULT_PINNED_COLUMNS);

    void Promise.all([
      saveColumnOrder(DEFAULT_COLUMN_ORDER),
      saveVisibleColumns(DEFAULT_VISIBLE_COLUMNS),
      savePinnedColumns(DEFAULT_PINNED_COLUMNS),
    ]);

    await fetchEquipmentLedger();
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
    setHasMoreData(true);
  };

  const getUniqueValues = (key) => {
    let values;
    if (key === 'updatedAtIso') {
      values = allData.map((item) => formatMMDDYYYY(item[key])).filter(Boolean);
    } else {
      values = allData.map((item) => item[key]).filter(Boolean);
    }
    return [...new Set(values)].sort().map((val) => ({ value: val, label: val }));
  };

  const filteredAndSortedData = useMemo(() => {
    let filtered = [...allData];

    if (searchTerm) {
      const query = searchTerm.toLowerCase();
      filtered = filtered.filter((item) =>
        Object.values(item).some((val) => val && val.toString().toLowerCase().includes(query))
      );
    }

    Object.keys(EMPTY_TEXT_FILTERS).forEach((key) => {
      const query = columnFilters[key];
      if (!query) return;
      const needle = query.toLowerCase();
      filtered = filtered.filter((item) => String(item[key] || '').toLowerCase().includes(needle));
    });

    Object.keys(columnFilterValues).forEach((key) => {
      const selected = columnFilterValues[key];
      if (!selected || selected.length === 0) return;
      if (key === 'lastModifiedDate') {
        filtered = filtered.filter((item) => selected.includes(formatMMDDYYYY(item.updatedAtIso)));
      } else {
        filtered = filtered.filter((item) => selected.includes(item[key]));
      }
    });

    if (dateRange.start || dateRange.end) {
      const startISO = toCalendarISO(dateRange.start);
      const endISO = toCalendarISO(dateRange.end);
      filtered = filtered.filter((item) => {
        const itemISO = toCalendarISO(item.dateIso || item.date);
        if (!itemISO) return false;
        if (startISO && itemISO < startISO) return false;
        if (endISO && itemISO > endISO) return false;
        return true;
      });
    }

    if (runTimeRange.min !== null || runTimeRange.max !== null) {
      filtered = filtered.filter((item) => {
        if (item.runTimeHours == null || item.runTimeHours === '') return false;
        const hours = Number(item.runTimeHours);
        if (!Number.isFinite(hours)) return false;
        if (runTimeRange.min !== null && hours < Number(runTimeRange.min)) return false;
        if (runTimeRange.max !== null && hours > Number(runTimeRange.max)) return false;
        return true;
      });
    }

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (sortConfig.key === 'runTimeHours') {
          aVal = a.runTimeHours == null || a.runTimeHours === '' ? -1 : Number(a.runTimeHours);
          bVal = b.runTimeHours == null || b.runTimeHours === '' ? -1 : Number(b.runTimeHours);
        } else if (['date', 'updatedAtIso'].includes(sortConfig.key)) {
          aVal = new Date(aVal).getTime() || 0;
          bVal = new Date(bVal).getTime() || 0;
        } else {
          aVal = (aVal || '').toString().toLowerCase();
          bVal = (bVal || '').toString().toLowerCase();
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [allData, searchTerm, columnFilters, columnFilterValues, dateRange, runTimeRange, sortConfig]);

  const displayedData = useMemo(() => {
    const sliced = filteredAndSortedData.slice(0, currentDisplayCount);
    const hasMore = currentDisplayCount < filteredAndSortedData.length;
    if (hasMoreData !== hasMore) setHasMoreData(hasMore);
    return sliced;
  }, [filteredAndSortedData, currentDisplayCount, hasMoreData]);

  const loadMoreData = React.useCallback(() => {
    if (loadingMore || !hasMoreData || loading) return;
    setLoadingMore(true);
    requestAnimationFrame(() => {
      setTimeout(() => {
        setCurrentDisplayCount((prev) => {
          const newCount = Math.min(prev + ITEMS_PER_PAGE, filteredAndSortedData.length);
          setHasMoreData(newCount < filteredAndSortedData.length);
          return newCount;
        });
        setLoadingMore(false);
      }, 300);
    });
  }, [loadingMore, hasMoreData, loading, filteredAndSortedData.length]);

  useEffect(() => {
    if (loading || loadingMore || !hasMoreData) return undefined;
    const root = tableScrollRef.current;
    const sentinel = loadingRef.current;
    if (!root || !sentinel) return undefined;

    let cancelled = false;
    const observer = new IntersectionObserver(
      (entries) => {
        if (cancelled) return;
        if (entries[0]?.isIntersecting) loadMoreData();
      },
      { root, threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [hasMoreData, loading, loadingMore, currentDisplayCount, loadMoreData]);

  useLayoutEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return undefined;
    const measure = () => {
      const clientWidth = el.clientWidth;
      const scrollWidth = el.scrollWidth;
      const overflow = Math.max(0, scrollWidth - clientWidth);
      const last = hScrollLastRef.current;
      if (
        Math.abs(overflow - last.overflow) < 1 &&
        Math.abs(scrollWidth - last.scrollWidth) < 1 &&
        Math.abs(clientWidth - last.clientWidth) < 1
      ) {
        return;
      }
      hScrollLastRef.current = { overflow, scrollWidth, clientWidth };
      setHScroll({ overflow, scrollWidth, clientWidth });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tableMinWidth, pinnedColumnsWidth, loading, displayedData.length]);

  const handleTableScroll = (event) => {
    const bar = hScrollbarRef.current;
    if (!bar) return;
    const next = event.currentTarget.scrollLeft;
    if (Math.abs(bar.scrollLeft - next) >= 1) bar.scrollLeft = next;
  };

  const handleHScrollbarScroll = (event) => {
    const scroller = tableScrollRef.current;
    if (!scroller) return;
    const next = event.currentTarget.scrollLeft;
    if (Math.abs(scroller.scrollLeft - next) >= 1) scroller.scrollLeft = next;
  };

  const handleTableWheelCapture = (event) => {
    if (!pinnedColumnsWidth) return;
    const pinnedEdgeEl = event.currentTarget.querySelector('.projects-col-pinned-last');
    if (!pinnedEdgeEl) return;
    const pinnedRight = pinnedEdgeEl.getBoundingClientRect().right;
    if (event.clientX > pinnedRight) return;
    const isHorizontal = Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey;
    if (!isHorizontal) return;
    event.preventDefault();
    event.stopPropagation();
  };

  const totalCount = filteredAndSortedData.length;

  const handleExportToExcel = async () => {
    const XLSX = await loadXLSX();
    if (!XLSX) {
      console.error('XLSX library not loaded');
      return;
    }
    const exportColumns = columns.filter((c) => visibleColumns.includes(c.key));
    const headers = exportColumns.map((c) => c.label);
    const rows = filteredAndSortedData.map((item) =>
      exportColumns.map((c) => {
        if (c.key === 'updatedAtIso') {
          return item.updatedAtIso ? formatMMDDYYYY(item.updatedAtIso) : '-';
        }
        if (c.key === 'runTimeHours') return formatHours(item.runTimeHours);
        return item[c.key] !== undefined && item[c.key] !== null && item[c.key] !== '' ? item[c.key] : '-';
      })
    );
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Equipment');
    XLSX.writeFile(wb, 'equipment_ledger.xlsx');
  };

  const handleOpenAddModal = () => {
    setEditingItem(null);
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (item, e) => {
    e.stopPropagation();
    setEditingItem(item);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
  };

  const handleOpenDeleteConfirm = (id, e) => {
    e.stopPropagation();
    setDeleteConfirm({ isOpen: true, id });
  };

  const handleCloseDeleteConfirm = () => {
    if (isDeleting) return;
    setDeleteConfirm({ isOpen: false, id: null });
  };

  const handleConfirmDelete = async () => {
    const { id } = deleteConfirm;
    if (!id) return;
    const baseUrl = process.env.REACT_APP_BACKEND_BASEURL;
    setIsDeleting(true);
    try {
      const response = await axios.delete(
        `${baseUrl}/api/miter/production-performance/equipment-ledger/${id}`,
        await getAuthConfig()
      );
      if (response.data && (response.data.status === 'success' || response.status === 200)) {
        if (selectedRowId === id) setSelectedRowId(null);
        setDeleteConfirm({ isOpen: false, id: null });
        await fetchEquipmentLedger(true);
      } else {
        throw new Error(response.data?.message || 'Failed to delete equipment entry');
      }
    } catch (err) {
      console.error('Error deleting equipment entry:', err);
      alert(err.response?.data?.message || err.message || 'Failed to delete equipment entry');
    } finally {
      setIsDeleting(false);
    }
  };

  const renderCellValue = (item, column) => {
    if (column.key === 'runTimeHours') return formatHours(item.runTimeHours);
    if (column.key === 'updatedAtIso') {
      return item.updatedAtIso ? formatFullTimestamp(item.updatedAtIso) : '-';
    }
    const cellValue = item[column.key];
    return cellValue || '-';
  };

  return (
    <div className="projects-page-card">
      <div className="projects-table-toolbar">
        <div className="projects-table-toolbar-row flex items-center gap-4 max-md:gap-2">
          <form className="projects-toolbar-search flex gap-2.5 items-center px-5 py-3 text-lg font-light bg-white border rounded-[31px] text-zinc-500 w-[30%] shadow-sm max-md:flex-1 max-md:w-auto max-md:bg-stone-50 max-md:border-0 max-md:rounded-full max-md:shadow-none">
            <img
              loading="lazy"
              src={Icon13}
              className="object-contain shrink-0 self-stretch my-auto aspect-square w-[27px]"
              alt=""
            />
            <input
              type="search"
              placeholder="Search"
              className="bg-transparent border-none outline-none w-full"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentDisplayCount(INITIAL_LOAD_COUNT);
                setHasMoreData(true);
              }}
            />
          </form>
          <button
            onClick={handleOpenAddModal}
            className="ml-2 px-6 py-2 rounded transition-colors max-md:hidden bg-red-700 text-white hover:bg-red-800"
          >
            + Add Equipment
          </button>
          <button
            onClick={handleOpenAddModal}
            aria-label="Add Equipment"
            className="projects-toolbar-add hidden max-md:flex items-center justify-center rounded-full w-10 h-10 bg-red-700 text-white"
          >
            <FiPlus className="w-5 h-5" />
          </button>

          <div className="flex-grow max-md:hidden"></div>

          <div className="projects-toolbar-actions">
            <div className="relative projects-toolbar-columns">
              <button
                type="button"
                ref={columnButtonRef}
                onClick={handleColumnSelectorClick}
                className="flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 max-md:border-0 max-md:bg-transparent max-md:rounded-none max-md:p-2 max-md:hover:bg-transparent max-md:shadow-none"
                title="Show/Hide Columns"
              >
                <FiColumns className="h-4 w-4" />
                <span className="ml-2 max-md:hidden">Columns</span>
              </button>
              <ColumnVisibilityDropdown
                ref={columnSelectorRef}
                isOpen={isColumnSelectorOpen}
                onClose={() => setIsColumnSelectorOpen(false)}
                columns={columns.filter((col) => col.key !== 'actions')}
                visibleColumns={visibleColumns}
                onToggleColumn={handleToggleColumn}
                onReorderColumn={handleReorderColumn}
                onReorderEnd={handleReorderColumnEnd}
                position={columnSelectorPosition}
              />
            </div>

            <button
              type="button"
              onClick={handleExportToExcel}
              disabled={totalCount === 0}
              className={`projects-toolbar-export flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 mr-2 max-md:border-0 max-md:bg-transparent max-md:rounded-none max-md:p-2 max-md:hover:bg-transparent max-md:shadow-none max-md:mr-0 ${totalCount === 0 ? 'cursor-not-allowed opacity-60' : ''}`}
              title="Download Excel"
            >
              <FiDownload className="h-4 w-4" />
            </button>

            <ResetButton onClick={handleResetData} tooltip="Reset" />
          </div>
        </div>
      </div>

      <div
        ref={tableScrollRef}
        className="projects-table-scroll projects-table-scroll--proxy-h"
        onScroll={handleTableScroll}
        onWheelCapture={handleTableWheelCapture}
      >
        <table
          className="projects-data-table"
          style={{
            width: `max(100%, ${tableMinWidth}px)`,
            minWidth: `${tableMinWidth}px`,
          }}
        >
          <colgroup>
            {visibleTableColumns.map((column) => {
              const isPinned = pinnedVisibleKeys.includes(column.key);
              return (
                <col
                  key={column.key}
                  style={
                    isPinned
                      ? { width: `${column.width}px`, minWidth: `${column.width}px` }
                      : {
                          width: `${(column.width / tableMinWidth) * 100}%`,
                          minWidth: `${column.width}px`,
                        }
                  }
                />
              );
            })}
          </colgroup>
          <thead className="bg-gray-50">
            <tr>
              {visibleTableColumns.map((column) => {
                const isPinned = pinnedVisibleKeys.includes(column.key);
                return (
                  <th
                    key={column.key}
                    style={getColumnStyle(column, { isHeader: true })}
                    className={`group/header px-4 py-3 max-md:whitespace-normal ${column.key !== 'actions' ? 'cursor-grab active:cursor-grabbing' : ''} transition-colors hover:bg-gray-100 ${
                      isPinned ? 'projects-col-pinned' : ''
                    } ${column.key === lastPinnedKey ? 'projects-col-pinned-last' : ''}`}
                    draggable={column.key !== 'actions'}
                    onDragStart={(e) => handleHeaderDragStart(e, column.key)}
                    onDragOver={(e) => handleHeaderDragOver(e, column.key)}
                    onDragEnd={handleHeaderDragEnd}
                  >
                    <div className="flex flex-col gap-2">
                      <div
                        className={`flex items-center text-sm font-medium text-gray-500 ${column.key === 'actions' ? 'justify-end' : 'justify-center cursor-pointer whitespace-nowrap'}`}
                        onClick={column.key !== 'actions' ? () => requestSort(column.key) : undefined}
                      >
                        <span className="truncate" title={column.label}>{column.label}</span>
                        {column.key !== 'actions' && (
                          <span className="ml-1 inline-flex w-4 shrink-0 items-center justify-center text-gray-500">
                            <SortIcon direction={sortIconDirection(sortConfig, column.key)} />
                          </span>
                        )}
                        {column.key !== 'actions' && (
                          <button
                            type="button"
                            data-pin-toggle
                            title={isPinned ? 'Unpin column' : 'Pin column to left'}
                            aria-label={isPinned ? `Unpin ${column.label}` : `Pin ${column.label}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleTogglePin(column.key);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            draggable={false}
                            className={`projects-pin-toggle ml-1 p-0.5 rounded shrink-0 transition-opacity ${
                              isPinned
                                ? 'is-pinned text-red-600 hover:text-red-800 hover:bg-red-50'
                                : 'text-blue-500 hover:text-blue-700 hover:bg-blue-50'
                            }`}
                          >
                            <span className="inline-flex">
                              {isPinned ? (
                                <MdPushPin className="h-3.5 w-3.5" />
                              ) : (
                                <MdOutlinePushPin className="h-3.5 w-3.5" />
                              )}
                            </span>
                          </button>
                        )}
                      </div>
                      {column.key !== 'actions' && (
                        <div className="relative" style={{ overflow: 'visible', zIndex: 1 }}>
                          {visibleColumns.includes(column.key) && (
                            <>
                              {column.type === 'search' && (
                                <input
                                  type="text"
                                  placeholder="Search"
                                  className="w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light text-zinc-500"
                                  value={columnFilters[column.key] || ''}
                                  onChange={(e) => {
                                    setColumnFilters((prev) => ({ ...prev, [column.key]: e.target.value }));
                                    setCurrentDisplayCount(INITIAL_LOAD_COUNT);
                                    setHasMoreData(true);
                                  }}
                                />
                              )}

                              {column.type === 'select' && (
                                <div
                                  className="relative"
                                  ref={activeFilterColumn === column.filterKey ? filterDropdownRef : null}
                                  style={getFilterWrapperStyle(activeFilterColumn === column.filterKey)}
                                >
                                  <button
                                    onClick={(e) => toggleFilterPopup(column.filterKey, e)}
                                    className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light flex items-center justify-between ${columnFilterValues[column.filterKey]?.length > 0 ? 'text-red-600 border-red-500' : 'text-zinc-500'}`}
                                  >
                                    <span>
                                      {columnFilterValues[column.filterKey]?.length > 0
                                        ? `${columnFilterValues[column.filterKey].length} selected`
                                        : 'Filter'}
                                    </span>
                                    <FiFilter className="ml-2" />
                                  </button>
                                  {activeFilterColumn === column.filterKey &&
                                    renderFilterPopup(
                                      <ColumnFilterDropdown
                                        isOpen={activeFilterColumn === column.filterKey}
                                        options={getUniqueValues(column.key)}
                                        selectedValues={columnFilterValues[column.filterKey] || []}
                                        onSelect={(value) => handleColumnFilterSelect(column.filterKey, value)}
                                        onClose={closeFilterPopup}
                                        maxHeight="60vh"
                                      />
                                    )}
                                </div>
                              )}

                              {column.type === 'date' && (
                                <div
                                  className="relative"
                                  ref={activeFilterColumn === 'date' ? filterDropdownRef : null}
                                  style={getFilterWrapperStyle(activeFilterColumn === 'date')}
                                >
                                  <button
                                    onClick={(e) => toggleFilterPopup('date', e)}
                                    className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light flex items-center justify-between ${dateRange.start || dateRange.end ? 'text-red-600' : 'text-zinc-500'}`}
                                  >
                                    <span className="truncate">Filter</span>
                                    <FiFilter className="ml-2 shrink-0" />
                                  </button>
                                  {activeFilterColumn === 'date' &&
                                    renderFilterPopup(
                                      <DateRangeFilterDropdown
                                        isOpen={activeFilterColumn === 'date'}
                                        currentRange={dateRange}
                                        onApply={(range) => {
                                          setDateRange(range);
                                          setCurrentDisplayCount(INITIAL_LOAD_COUNT);
                                          setHasMoreData(true);
                                        }}
                                        onClose={closeFilterPopup}
                                      />
                                    )}
                                </div>
                              )}

                              {column.type === 'range' && (
                                <div
                                  className="relative"
                                  ref={activeFilterColumn === column.filterKey ? filterDropdownRef : null}
                                  style={getFilterWrapperStyle(activeFilterColumn === column.filterKey)}
                                >
                                  <button
                                    onClick={(e) => toggleFilterPopup(column.filterKey, e)}
                                    className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:border-red-500 text-center font-light flex items-center justify-between ${runTimeRange.min !== null || runTimeRange.max !== null ? 'text-red-600' : 'text-zinc-500'}`}
                                  >
                                    <span>Filter</span>
                                    <FiFilter className="ml-2" />
                                  </button>
                                  {activeFilterColumn === column.filterKey &&
                                    renderFilterPopup(
                                      <RangeFilterDropdown
                                        isOpen={activeFilterColumn === column.filterKey}
                                        currentRange={runTimeRange}
                                        label={`Filter by ${column.label}`}
                                        minLabel="Min Hours"
                                        maxLabel="Max Hours"
                                        minPlaceholder="Min"
                                        maxPlaceholder="Max"
                                        onApply={(range) => {
                                          setRunTimeRange(range);
                                          setCurrentDisplayCount(INITIAL_LOAD_COUNT);
                                          setHasMoreData(true);
                                        }}
                                        onClose={closeFilterPopup}
                                      />
                                    )}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={visibleTableColumns.length} className="p-0">
                  <div
                    className="projects-table-loading-inline"
                    style={hScroll.clientWidth > 0 ? { width: hScroll.clientWidth } : undefined}
                    role="status"
                    aria-live="polite"
                  >
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-700" />
                    <span className="ml-2">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td colSpan={visibleTableColumns.length} className="text-center py-4 text-red-700">
                  Failed to load equipment: {error}
                </td>
              </tr>
            ) : displayedData.length === 0 ? (
              <tr>
                <td colSpan={visibleTableColumns.length} className="text-center py-4">No equipment entries found</td>
              </tr>
            ) : (
              displayedData.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => setSelectedRowId(selectedRowId === item.id ? null : item.id)}
                  className={`hover:bg-gray-50 cursor-pointer ${selectedRowId === item.id ? 'bg-red-50' : ''}`}
                >
                  {visibleTableColumns.map((column) => {
                    const isPinned = pinnedVisibleKeys.includes(column.key);
                    if (column.key === 'actions') {
                      return (
                        <td
                          key="actions"
                          style={getColumnStyle(column)}
                          className="px-4 py-4 text-sm font-medium"
                        >
                          <div className="flex items-center justify-end gap-1">
                            <button
                              onClick={(e) => handleOpenEditModal(item, e)}
                              className="text-blue-600 hover:text-blue-800 p-1 rounded hover:bg-blue-50 transition"
                              title="Edit"
                            >
                              <FiEdit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={(e) => handleOpenDeleteConfirm(item.id, e)}
                              className="text-red-600 hover:text-red-800 p-1 rounded hover:bg-red-50 transition"
                              title="Delete"
                            >
                              <FiTrash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      );
                    }

                    return (
                      <TooltipCell
                        key={column.key}
                        content={renderCellValue(item, column)}
                        style={getColumnStyle(column)}
                        className={`text-gray-900 max-md:whitespace-normal break-words${
                          isPinned ? ' projects-col-pinned' : ''
                        }${column.key === lastPinnedKey ? ' projects-col-pinned-last' : ''}`}
                        align="left"
                      />
                    );
                  })}
                </tr>
              ))
            )}
            {!loading && !error && displayedData.length > 0 && hasMoreData && (
              <tr ref={loadingRef}>
                <td colSpan={visibleTableColumns.length} className="p-0">
                  <div
                    className="projects-table-loading-inline"
                    style={hScroll.clientWidth > 0 ? { width: hScroll.clientWidth } : undefined}
                  >
                    {loadingMore ? (
                      <>
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-700" />
                        <span className="ml-2">Loading more...</span>
                      </>
                    ) : (
                      <span className="text-gray-500">Scroll down to load more</span>
                    )}
                  </div>
                </td>
              </tr>
            )}
            {!loading && !error && displayedData.length > 0 && !hasMoreData && (
              <tr>
                <td colSpan={visibleTableColumns.length} className="p-0">
                  <div
                    className="projects-table-loading-inline"
                    style={hScroll.clientWidth > 0 ? { width: hScroll.clientWidth } : undefined}
                  >
                    <span className="text-gray-500">No more data to load</span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {hScroll.overflow > 0 && (
        <div className="projects-h-scrollbar-rail">
          <div
            ref={hScrollbarRef}
            onScroll={handleHScrollbarScroll}
            className="projects-h-scrollbar"
            style={{
              marginLeft: `${pinnedColumnsWidth}px`,
              width: `${Math.max(hScroll.clientWidth - pinnedColumnsWidth, 0)}px`,
            }}
          >
            <div style={{ height: '1px', width: `${Math.max(hScroll.scrollWidth - pinnedColumnsWidth, 0)}px` }} />
          </div>
        </div>
      )}
      <RowsIndicator loadedCount={displayedData.length} totalCount={totalCount} />

      <EquipmentLedgerModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        editingItem={editingItem}
        getAuthConfig={getAuthConfig}
        onSaved={() => fetchEquipmentLedger(true)}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Equipment"
        message="Are you sure you want to delete this equipment entry? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onClose={handleCloseDeleteConfirm}
        isLoading={isDeleting}
        isDestructive
      />
    </div>
  );
}

export default EquipmentManagement;
